import Image from 'next/image'
import Link from 'next/link'
import type { Author } from '@/payload-types'
import { mediaUrl } from '@/lib/seo/jsonld'
import { hostOf } from '@/lib/sourceClass'
import { formatDate, DATE_LONG } from '@/lib/format'
import { BOX_EYEBROW, TAIL_BOX } from './chip'

/**
 * La firma in coda all'articolo: chi è l'autore e che cosa ha fatto su QUESTA pagina.
 *
 * ERA UN CERCHIO CON L'INIZIALE dentro <ArticleView> — nome, data, bio — e l'iniziale era l'unica
 * cosa che si vedeva anche quando l'autore aveva una foto: il campo `avatar` esiste in Authors.ts
 * dal principio, è popolato dal `depth: 2` di getPostBySlug, ed era renderizzato solo nel JSON-LD
 * (jsonld.ts, `image` della Person) — cioè lo vedeva Google e non il lettore. Qui la foto c'è, e il
 * ripiego sull'iniziale resta per gli autori che non l'hanno caricata.
 *
 * PERCHÉ DICHIARA LA REVISIONE, e da dove viene quella frase. La testata (<Provenance>) apre con
 * «Testo scritto con l'intelligenza artificiale e revisionato dall'autore», ma la testata scrolla
 * via nella prima schermata: chi arriva in fondo — dopo il corpo, i tag e le fonti — ha davanti una
 * pagina che ha appena dichiarato di essere scritta da un modello e nessuno che se ne assuma la
 * responsabilità. La dichiarazione va dove c'è il nome e la faccia della persona, che è il punto in
 * cui vale qualcosa.
 *
 * ATTENZIONE A COSA LA REGGE, perché è la stessa eccezione che <Provenance> documenta e va tenuta in
 * chiaro: NESSUN campo registra la revisione umana. `provenance.verifiedAt` lo scrive il worker alla
 * generazione (src/worker/index.ts) — è la verifica automatica, non una persona che ha riletto — e
 * per questo qui NON viene usato. Le date di questa scheda sono le uniche due che descrivono un atto
 * editoriale reale: `publishedAt` (il momento in cui l'articolo è uscito sotto quella firma) e
 * `contentUpdatedAt` (l'ultimo aggiornamento del testo). La frase resta quindi un'asserzione
 * dell'editore, non un dato derivato: per derivarla servono un `reviewedBy`/`reviewedAt` nel gruppo
 * `provenance` (Posts.ts) scritti alla pubblicazione, e questa stringa condizionata a quelli.
 *
 * IL RIQUADRO È QUELLO DI <PracticalBox> (`TAIL_BOX`), e prima non lo era: aveva un linguaggio suo —
 * fondo `zinc-50/70`, bordo `zinc-100`, occhiello grigio — con dentro un cerchio in `bg-accent-soft`,
 * cioè la tinta del riquadro pratico dentro una scatola grigia, due sezioni più in basso di quel
 * riquadro. Erano tre vestiti per quattro blocchi che si leggono di fila. Ora la coda ha due soli
 * registri e questo è il secondo riquadro: apre <PracticalBox>, chiude questo.
 *
 * A DISTINGUERLI PENSANO L'OCCHIELLO E IL CERCHIO. Il cerchio dell'avatar è l'unica forma che nel
 * resto della coda non compare, ed è anche il motivo per cui il ripiego sull'iniziale è su fondo
 * BIANCO e non in tinta: `bg-accent-soft` è il fondo del riquadro, un cerchio di quel colore dentro
 * quel riquadro sarebbe invisibile.
 */
export default function AuthorCard({
  author,
  publishedAt,
  contentUpdatedAt,
}: {
  author: Author
  publishedAt?: string | null
  /** Ultimo aggiornamento del corpo: mostrato SOLO se posteriore alla pubblicazione. */
  contentUpdatedAt?: string | null
}) {
  const avatar = mediaUrl(author.avatar)
  const published = formatDate(publishedAt, DATE_LONG)
  // Stesso clamp dei metadata OG (page.tsx): un `contentUpdatedAt` anteriore alla pubblicazione è
  // rumore del salvataggio, non una revisione, e annunciarlo come «ultimo controllo» sarebbe una
  // data più vecchia di quella che le sta accanto.
  const updatedISO = contentUpdatedAt && publishedAt && contentUpdatedAt > publishedAt ? contentUpdatedAt : null
  const updated = formatDate(updatedISO, DATE_LONG)
  const socials = (author.social ?? []).filter((s) => s?.url)

  return (
    // `aria-label` col nome dell'autore e non un `aria-labelledby`: lo scroll infinito monta N
    // <ArticleView> nella stessa pagina, quindi un id fisso sarebbe duplicato N volte (è il vincolo
    // che <SourcesBlock>, <TagsBlock> e <PracticalBox> documentano ciascuno a modo suo).
    <section className={TAIL_BOX} aria-label={`Autore: ${author.name}`}>
      <div className="flex items-center gap-4 sm:gap-5">
        {avatar ? (
          // `alt` VUOTO di proposito: il nome sta nella riga accanto, quindi un alt col nome lo
          // farebbe annunciare due volte di fila a chi usa uno screen reader. La foto qui è
          // l'illustrazione della firma, non un'informazione che manca al testo.
          <Image
            src={avatar}
            alt=""
            width={72}
            height={72}
            // 60 e non un valore a piacere: `images.qualities` in next.config.mjs dichiara [60, 75]
            // e una quality fuori lista fa rispondere 400 all'optimizer — l'avatar sarebbe un buco.
            // 60 è la misura dichiarata per card e thumbnail, che è esattamente cos'è questa foto.
            quality={60}
            // NIENTE `sizes`: su un'immagine a misura fissa Next genera il srcset 1x/2x dalla
            // `width` (72 e 144). Con `sizes` passerebbe a un srcset in `w` su tutte le misure
            // configurate, cioè decine di URL per un cerchio da 72px.
            className="size-[72px] shrink-0 rounded-full object-cover ring-1 ring-accent/15"
          />
        ) : (
          <div
            aria-hidden
            className="grid size-[72px] shrink-0 place-items-center rounded-full bg-white text-2xl font-bold text-accent-strong ring-1 ring-accent/15"
          >
            {author.name?.charAt(0) ?? 'A'}
          </div>
        )}

        <div className="min-w-0">
          <h2 className={BOX_EYEBROW}>A cura di</h2>
          {/* `text-base` e non `text-lg`: dentro un riquadro in corpo `text-sm` il nome è già il
              testo più grande e più scuro: al passo successivo pesava come un titolo di sezione del
              corpo, che è il rango sopra. */}
          <p className="mt-1 text-base font-bold leading-tight text-ink">
            {author.slug ? (
              <Link href={`/autore/${author.slug}/`} className="transition-colors hover:text-accent-strong">
                {author.name}
              </Link>
            ) : (
              author.name
            )}
          </p>
          {author.bio ? <p className="mt-1 leading-relaxed text-zinc-600">{author.bio}</p> : null}

          {socials.length ? (
            // `role="list"` esplicito: con `display: flex` Safari/VoiceOver toglie all'elemento il
            // ruolo di lista (stessa ragione dei chip in <TagsBlock> e <SourcesBlock>).
            <ul role="list" className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
              {socials.map((s) => (
                <li key={s.id ?? s.url}>
                  {/* `rel="me"`: è il link che dichiara «questo profilo è la stessa persona che
                      firma qui», ed è ciò che rende il collegamento verificabile invece che
                      decorativo. `nofollow` no — a differenza delle fonti, questi non sono
                      rimandi documentari ma l'identità dell'autore. */}
                  <a
                    href={s.url}
                    rel="me noopener"
                    target="_blank"
                    className="transition-colors hover:text-accent-strong"
                  >
                    {hostOf(s.url) || s.url}
                  </a>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>

      {/* LA RIGA DELLA REVISIONE STA SOTTO, STACCATA DAL FILETTO DEL RIQUADRO, ed è l'idioma che
          <PracticalBox> usa per il suo avviso di accessibilità: là e qui è la stessa cosa — una
          frase che diciamo NOI al lettore, non un dato letto da qualcuno. Stesso posto, stesso
          filetto in tinta (un `border-zinc-100` dentro un blocco colorato si legge come una crepa),
          stesso registro. Prima stava di fianco all'avatar, dentro la colonna del nome, e per una
          frase lunga una riga e mezza era la colonna sbagliata.

          Le frasi stanno su UNA riga di sorgente ciascuna, spazi inclusi: JSX toglie il whitespace
          di bordo dei nodi di testo mandati a capo, ed è il difetto che <Provenance> ha già pagato
          in pagina («…istituzionale.L'accessibilità…»). */}
      <p className="mt-4 flex gap-2 border-t border-accent/15 pt-3 leading-relaxed text-zinc-600">
        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden className="mt-[3px] size-4 shrink-0 text-accent-strong">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z"
            clipRule="evenodd"
          />
        </svg>
        <span>
          {published ? (
            <>Ha revisionato questo articolo e ne ha approvato la pubblicazione il <time dateTime={publishedAt ?? undefined}>{published}</time>.</>
          ) : (
            <>Ha revisionato questo articolo e ne ha approvato la pubblicazione.</>
          )}
          {updated ? (
            <> Ultimo controllo dei contenuti il <time dateTime={updatedISO ?? undefined}>{updated}</time>.</>
          ) : null}
        </span>
      </p>
    </section>
  )
}
